import { useState, useEffect } from 'react'
import { FetchPolicy } from '@apollo/client'
import { useInventoryLocationsQuery, Permission, InventoryLocationsQuery, EsBranch, CursorPaging } from '../graphql'

/**
 * What the InventoryLocations query actually returns.
 *
 * Deliberately not the generated `InventoryLocation` schema entity: that type
 * requires every non-null field the server declares, including ones this query
 * never selects, so typing against it breaks whenever the schema gains a
 * required field the app has no use for.
 */
export type InventoryLocationNode = InventoryLocationsQuery['inventoryLocations']['edges'][number]['node']

export type InventoryLocationWithLabel = { label: string } & InventoryLocationNode

export type BranchWithInventoryLocations = EsBranch & {
	label: string,
	inventoryLocations: InventoryLocationWithLabel[],
}

export function sortByName( a: { name: string, defaultLocation?: boolean }, b: { name: string, defaultLocation?: boolean } ) {
	if ( a.defaultLocation || b.defaultLocation ) {
		if ( a.defaultLocation ) {
			return -1
		} else {
			return 1
		}
	}

	const aName = a.name.toLowerCase()
	const bName = b.name.toLowerCase()

	if ( aName < bName ) {
		return -1
	}
	if ( aName > bName ) {
		return 1
	}
	return 0
}

export function groupInventoryLocationsByBranch( inventoryLocations: InventoryLocationNode[] ) {
	const branches: BranchWithInventoryLocations[] = []

	inventoryLocations.forEach( ( inventoryLocation ) => {
		const branchIndex = branches.findIndex( ( branch ) => branch.id === inventoryLocation.branch.id )
		if ( branchIndex > -1 ) {
			branches[ branchIndex ].inventoryLocations.push( { ...inventoryLocation, label: inventoryLocation.name } )
		} else {
			branches.push( {
				...inventoryLocation.branch,
				label: inventoryLocation.branch.name,
				inventoryLocations: [
					{ ...inventoryLocation, label: inventoryLocation.name }
				]
			} )
		}
	} )

	branches.sort( sortByName ).forEach( ( branch ) => branch.inventoryLocations.sort( sortByName ) )

	return branches
}

export function filterInventoryLocations( inventoryLocations: InventoryLocationNode[], searchString: string ) {
	const filteredInventoryLocations: InventoryLocationNode[] = []
	const searchExpr = new RegExp( searchString, 'i' )

	inventoryLocations.forEach( ( inventoryLocation ) => {
		if ( searchExpr.exec( inventoryLocation.name ) ) {
			filteredInventoryLocations.push( inventoryLocation )
		} else if ( inventoryLocation.branch?.name ) {
			if ( searchExpr.exec( inventoryLocation.branch.name ) ) {
				filteredInventoryLocations.push( inventoryLocation )
			}
		}
	} )

	return filteredInventoryLocations
}

/**
 * Append a freshly fetched page of locations to the already-accumulated list while
 * fetching all pages. Deduping by id keeps the result correct if the same page is
 * applied more than once (e.g. React invoking the effect twice for the same data), and
 * returning the same reference when there is nothing new avoids an unnecessary re-render.
 */
export function accumulateLocations( previousLocations: InventoryLocationNode[], newPage: InventoryLocationNode[] ) {
	const seenIds = new Set( previousLocations.map( ( location ) => location.id ) )
	const additions = newPage.filter( ( location ) => !seenIds.has( location.id ) )
	return additions.length ? [ ...previousLocations, ...additions ] : previousLocations
}

export default function useInventoryLocationsSearch( search: string, debounceKey = 'inventoryLocationsQuery-debounce', fetchPolicy: FetchPolicy = 'no-cache', permissions?: Permission[], paging?: CursorPaging, getAll?: boolean, skip = false, isWarehouse?: boolean ) {
	const [ filteredInventoryLocations, setFilteredInventoryLocations ] = useState<InventoryLocationNode[]>( [] )
	const [ allInventoryLocations, setAllInventoryLocations ] = useState<InventoryLocationNode[]>( [] )
	const [ isLoading, setIsLoading ] = useState( true )

	const variables: { permissions?: Permission[], paging?: CursorPaging, search?: string, isWarehouse?: boolean } = {}
	if ( permissions ) {
		variables.permissions = permissions
	}
	if ( isWarehouse !== undefined ) {
		variables.isWarehouse = isWarehouse
	}
	if ( paging ) {
		variables.paging = paging
	}
	if ( getAll ) {
		variables.paging = { ...paging, first: 1000 }
	} else if ( search ) {
		// Use server-side search when not fetching all locations
		variables.search = search
		// Fetch up to 100 items to allow scrolling
		variables.paging = { ...paging, first: 100 }
	}
	const queryParams = {
		context: { debounceKey },
		fetchPolicy,
		...( variables ? { variables } : {} ),
		// Skip when the caller opts out, or when not fetching all and no search term provided
		skip: skip || ( !getAll && !search ),
	}

	const { data, loading, refetch, ...result } = useInventoryLocationsQuery( queryParams )

	useEffect( () => {
		if ( !getAll ) {
			setIsLoading( false )
		} else if ( data?.inventoryLocations.pageInfo.hasNextPage ) {
			setIsLoading( true )
		} else {
			setIsLoading( false )
		}
	}, [ data ] )

	useEffect( () => {
		if ( loading ) {
			return
		}

		const nodes: InventoryLocationNode[] = data?.inventoryLocations
			? data.inventoryLocations.edges.map( ( edge ) => edge.node as InventoryLocationNode )
			: []

		if ( !getAll ) {
			// Server-side search already returns the page we want, so just replace.
			setAllInventoryLocations( nodes )
			setFilteredInventoryLocations( nodes )
			return
		}

		// Accumulate pages with a functional updater (rather than a stale closure value),
		// deduping by id so the fetch-all waterfall can neither drop nor duplicate a page
		// if this effect runs more than once for the same data. Deriving the filtered list
		// is left to the effect below, keyed on allInventoryLocations.
		setAllInventoryLocations( ( prev ) => accumulateLocations( prev, nodes ) )

		if ( data?.inventoryLocations.pageInfo.hasNextPage ) {
			refetch( { ...variables, paging: { first: 1000, after: data.inventoryLocations.pageInfo.endCursor } } )
		}
	}, [ data, loading ] )

	useEffect( () => {
		// Only use client-side filtering when getAll is true (e.g., main inventory view).
		// Otherwise, server-side search is used via the search parameter, and the filtered
		// list is set alongside the page results in the effect above.
		if ( getAll ) {
			setFilteredInventoryLocations( filterInventoryLocations( allInventoryLocations, search ) )
		}
	}, [ search, getAll, allInventoryLocations ] )

	const refetchAll = async() => {
		setIsLoading( true )
		setAllInventoryLocations( [] )
		// Restart from the first page. Calling refetch() with no arguments would reuse the
		// variables from the last page of the fetch-all waterfall (which carry an `after`
		// cursor), re-querying from the end of the list and returning few or no records.
		// Await refetching so callers (e.g., RefreshButton) can track loading completion.
		await refetch( variables )
	}

	return {
		inventoryLocations: allInventoryLocations,
		filteredInventoryLocations: filteredInventoryLocations as InventoryLocationNode[],
		loading: isLoading || loading,
		refetch: refetchAll,
		...result,
	}
}
